import axios, { AxiosError } from 'axios';

import { ResponseTO } from '../types/response-to.type';
import { PaginatedQuery, PaginatedResult } from '../types/paginated-query.type';
import { Credentials } from '../types/credentials.type';
import { OperatorCreation } from '../types/operator-creation.type';
import { Company } from '../types/company.type';
import { User, UserCreation, UserSummary } from '../types/user.type';
import { Operator, RolePermission } from '../types/operator.type';

import { addQueryParams, handleResponseError } from './service';

const BASE_URL = process.env.REACT_APP_USERS_URL;
const ADMIN_URL = process.env.REACT_APP_ADMIN_USERS_URL;
const AUTH_TOKEN = process.env.REACT_APP_API_TOKEN;

const getUserById = async (id: number): Promise<ResponseTO> => {
    try {
        const response = await axios.get(`${BASE_URL}/v1/users/${id}`, {
            headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
        });
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getAll = async (
    queryParams?: PaginatedQuery & { [key: string]: any }
): Promise<PaginatedResult<UserSummary> | null> => {
    try {
        const inlineParams = addQueryParams(queryParams);
        const response = await axios.get(
            `${ADMIN_URL}/v2/users/${inlineParams}`,
            {
                headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
            }
        );
        return response.data;
    } catch (err) {
        console.error('[!] Error getting users list');
        return null;
    }
};

const getUserVehicles = async (id: number): Promise<ResponseTO> => {
    try {
        const response = await axios.get(
            `${BASE_URL}/v1/users/${id}/vehicles`,
            {
                headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const searchUser = async (query: string): Promise<ResponseTO> => {
    console.log('User Service: searching user');
    try {
        const response = await axios.get(
            `${BASE_URL}/v1/users/search?query=${query}`,
            { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const logIn = async (data: Credentials): Promise<ResponseTO> => {
    console.log('User Service: login operator');
    try {
        const response = await axios.post(
            `${BASE_URL}/v1/auth/operators`,
            data,
            {
                headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getOperators = async (
    params?: {
        isActive: boolean;
        role: number;
    },
    token?: string
): Promise<ResponseTO> => {
    console.log('User Service: get operators');
    try {
        const inlineParams = addQueryParams(params);
        const response = await axios.get(
            `${BASE_URL}/v1/operators${inlineParams}`,
            {
                headers: { Authorization: `Bearer ${token ?? AUTH_TOKEN}` },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getOperatorById = async (
    id: number,
    token: string
): Promise<ResponseTO<Operator>> => {
    console.log('Get operator details by ID');
    try {
        const response = await axios.get(`${ADMIN_URL}/operators/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getMyProfile = async (token: string): Promise<ResponseTO> => {
    console.log('User Service: get my profile');
    try {
        const response = await axios.get(`${BASE_URL}/v1/operators/me`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const deleteOperator = async (
    id: number,
    token: string
): Promise<ResponseTO> => {
    console.log('User Service: delete operator');
    try {
        const response = await axios.delete(`${ADMIN_URL}/operators/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const createOperator = async (
    data: OperatorCreation,
    token: string
): Promise<ResponseTO> => {
    console.log('User Service: create operator');
    try {
        const response = await axios.post(`${ADMIN_URL}/operators`, data, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getRoles = async (token: string): Promise<ResponseTO> => {
    console.log('User Service: get roles');
    try {
        const response = await axios.get(`${ADMIN_URL}/roles`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const searchCompaniesByName = async (
    query: string,
    token: string
): Promise<ResponseTO<Company[]>> => {
    console.log('User Service: search companies by name query');
    try {
        console.log(`${ADMIN_URL}/companies/search?name=${query}`);
        const response = await axios.get(
            `${ADMIN_URL}/companies/search?name=${query}`,
            {
                headers: { Authorization: `Bearer ${token}` },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const createUser = async (
    data: UserCreation,
    auth: { token: string }
): Promise<ResponseTO<User>> => {
    console.log('User Service: create user');
    try {
        const response = await axios.post(`${BASE_URL}/v1/users`, data, {
            headers: { Authorization: `Bearer ${auth.token}` },
        });
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const getPermissions = async (
    token: string
): Promise<ResponseTO<RolePermission[]>> => {
    console.log('Get permissions');
    try {
        const response = await axios.get(`${ADMIN_URL}/permissions`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

const requestPasswordRecovery = async (
    id: number,
    token: string
): Promise<ResponseTO<RolePermission[]>> => {
    console.log('Request Password Recovery');
    try {
        const response = await axios.get(
            `${ADMIN_URL}/operators/${id}/reset-password`,
            {
                headers: { Authorization: `Bearer ${token}` },
            }
        );
        return response.data;
    } catch (err) {
        return handleResponseError(err as AxiosError);
    }
};

export const userSdk = {
    getUserById,
    getAll,
    createUser,
    getUserVehicles,
    searchUser,
    logIn,
    getOperators,
    getMyProfile,
    deleteOperator,
    createOperator,
    getRoles,
    searchCompaniesByName,
    getOperatorById,
    getPermissions,
    requestPasswordRecovery,
};
